/**
 * Worked example for the acquisition process, step "Bewertung".
 *
 * Every figure below is taken verbatim from the decision paper
 * *"2018-07-10 E-Vorlage Ludwigstraße 8 — Halle"* (CapitalBay, Entscheidungs-
 * vorlage, 10.07.2018, incl. the Legal-DD report by Schmidt-Morsbach & Partner
 * of 22.05.2018). Nothing here is computed, estimated or carried over from the
 * property the user has selected — it is a sample that shows what a filled
 * perspective looks like, and the UI labels it as such wherever it is shown.
 *
 * The values stay in the language of the source document; they are quoted
 * document content, not interface text, and are therefore not translated.
 */

export interface SampleMetric {
    label: string;
    value: string;
}

export interface SampleSection {
    title: string;
    metrics: SampleMetric[];
}

export interface SamplePerspectiveContent {
    /** One line that sums the perspective up. */
    headline: string;
    /** The three figures the tile itself shows. */
    highlights: SampleMetric[];
    /** Everything the drill-down shows. */
    sections: SampleSection[];
    /** Findings that are text rather than a figure. */
    notes?: string[];
}

export const sampleSource = {
    object: 'Halle, Ludwigstraße 8 (06110 Halle)',
    document: 'Entscheidungsvorlage ID_21_Hal, CapitalBay, 10.07.2018',
};

const microLocation: SamplePerspectiveContent = {
    headline: 'Sanierter Altbau, hohes Mietniveau in guter Mikrolage',
    highlights: [
        { label: 'Bezirk', value: 'Süd' },
        { label: 'Baujahr', value: '1930' },
        { label: 'Grundstück', value: '373 m²' },
    ],
    sections: [
        {
            title: 'Lage',
            metrics: [
                { label: 'Bezirk', value: 'Süd' },
                { label: 'Mietspiegel-Lage', value: '–' },
                { label: 'Mietpreisbremse', value: 'Nein' },
            ],
        },
        {
            title: 'Objektmerkmale',
            metrics: [
                { label: 'Grundstücksgröße', value: '373 m²' },
                { label: 'Anzahl Gebäude', value: '1' },
                { label: 'Baujahr', value: '1930' },
                { label: 'Sanierung / Modernisierung', value: '2012' },
                { label: 'Dachausbau', value: 'Ja' },
                { label: 'Aufzug', value: 'Nein' },
                { label: 'Balkone', value: 'Ja' },
                { label: 'Heizung', value: 'Gaszentralheizung' },
            ],
        },
        {
            title: 'Öffentlich-rechtliche Angaben',
            metrics: [
                { label: 'Denkmalschutz', value: 'Ja' },
                { label: 'Förderung', value: 'Nein' },
                { label: 'Besonderes Städtebaurecht', value: '–' },
            ],
        },
    ],
    notes: [
        'Grundbuch des Amtsgerichts Halle (Saale), Gemarkung Ort, Blatt 27026: Flur 1, Flurstück 704/14.',
        'Abt. II lastenfrei; die Belastungen in Abt. III werden laut Kaufvertragsentwurf vor Übertragung gelöscht.',
    ],
};

const macroLocation: SamplePerspectiveContent = {
    headline: 'Markt Halle (Saale): Marktmiete 6,50 €/m², Leerstand 8,00 %',
    highlights: [
        { label: 'Marktmiete', value: '6,50 €/m²' },
        { label: 'Marktleerstand', value: '8,00 %' },
        { label: 'Einwohner 2025', value: '236.991' },
    ],
    sections: [
        {
            title: 'Marktdaten',
            metrics: [
                { label: 'Einwohner — Prognose 2025', value: '236.991' },
                { label: 'Entwicklung Einwohner', value: '−1,7 %' },
                { label: 'Marktmiete', value: '6,50 €/m²' },
                { label: 'Marktleerstand', value: '8,00 %' },
            ],
        },
        {
            title: 'Einordnung des Objekts',
            metrics: [
                { label: 'Ist-Miete Wohnen Ø', value: '7,72 €/m²' },
                { label: 'Leerstand Objekt', value: '26,5 %' },
                { label: 'Region', value: 'Halle' },
            ],
        },
    ],
    notes: [
        'Die Ist-Miete des Objekts liegt über der Marktmiete, der Leerstand über dem Marktleerstand — beides Größen, die der Businessplan über die Laufzeit annähert.',
    ],
};

const letting: SamplePerspectiveContent = {
    headline: '7 Wohneinheiten, 616 m², 26,5 % Leerstand',
    highlights: [
        { label: 'Ist-Miete Ø', value: '7,72 €/m²' },
        { label: 'Leerstand', value: '26,5 %' },
        { label: 'Ist-Miete p.a.', value: '41.939 €' },
    ],
    sections: [
        {
            title: 'Wohnen gesamt',
            metrics: [
                { label: 'Einheiten', value: '7' },
                { label: 'Fläche', value: '616 m²' },
                { label: 'Ø Fläche', value: '88 m²' },
                { label: 'Ist-Miete Ø', value: '7,72 €/m²' },
                { label: 'Ist-Miete p.a.', value: '41.939 €' },
            ],
        },
        {
            title: 'Leerstand',
            metrics: [
                { label: 'Einheiten', value: '1' },
                { label: 'Fläche', value: '163 m²' },
                { label: 'Aktueller Leerstand', value: '26,5 %' },
                { label: 'Basis-Leerstand', value: '2,0 %' },
                { label: 'Abbau bis Ende', value: '12 Monate' },
                { label: 'Fluktuation p.a.', value: '10,0 %' },
            ],
        },
        {
            title: 'Vermietungen nach Mietbeginn',
            metrics: [
                { label: '2017 — 1 Einheit, 73 m²', value: '8,42 €/m² · 7.380 € p.a.' },
                { label: '2018 — 1 Einheit, 84 m²', value: '7,63 €/m² · 7.692 € p.a.' },
                { label: 'Gesamt — 2 Einheiten, 157 m²', value: '8,00 €/m² · 15.072 € p.a.' },
            ],
        },
    ],
    notes: [
        'Es bestehen 6 Wohnraum-Mietverträge. Das Dachgeschoss wird derzeit vom Verkäufer bewohnt; der Auszug ist zum 23.08.2018 geplant, ein Neuvermietungsangebot liegt vor.',
        'Drei Wohnungen (WE 04, WE 05, WE 06) sind mit Einbauküchen übergeben worden; das Eigentum an den Küchen ist noch zu klären.',
        'Kautionsnachweis für die Mieter Thielemann/Pfeiffer fehlt; mit Mieter Hundt ist eine 4-Monatsratenzahlung vereinbart.',
    ],
};

const acquisitionCosts: SamplePerspectiveContent = {
    headline: 'Ankaufspreis brutto 1.146.303 € (1.860 €/m²)',
    highlights: [
        { label: 'Ankaufspreis netto', value: '1.000.000 €' },
        { label: 'Nebenkosten', value: '14,6 %' },
        { label: 'Nettoanfangsrendite', value: '3,66 %' },
    ],
    sections: [
        {
            title: 'Kaufpreis',
            metrics: [
                { label: 'Ankaufspreis netto', value: '1.000.000 €' },
                { label: 'je m²', value: '1.623 €/m²' },
                { label: 'Vervielfältiger (Ist)', value: '23,84x' },
                { label: 'Vervielfältiger (Soll)', value: '23,27x' },
                { label: 'Bruttoanfangsrendite', value: '4,19 %' },
            ],
        },
        {
            title: 'Nebenkosten',
            metrics: [
                { label: 'Grunderwerbsteuer (5,0 %)', value: '50.000 €' },
                { label: 'Notar & Grundbuch (1,3 %)', value: '12.852 €' },
                { label: 'Maklergebühr (5,0 %)', value: '59.500 €' },
                { label: 'Technische DD (0,1 %)', value: '1.428 €' },
                { label: 'Rechtliche DD (0,8 %)', value: '7.735 €' },
                { label: 'Steuerliche DD (0,2 %)', value: '2.380 €' },
                { label: 'Vergütung CB (1,0 %)', value: '11.900 €' },
                { label: 'Reserve (0,1 %)', value: '508 €' },
                { label: 'Nebenkosten gesamt (14,6 %)', value: '146.303 €' },
            ],
        },
        {
            title: 'Ankaufspreis brutto',
            metrics: [
                { label: 'Ankaufspreis brutto', value: '1.146.303 €' },
                { label: 'je m²', value: '1.860 €/m²' },
                { label: 'Vervielfältiger (Ist)', value: '27,33x' },
                { label: 'Vervielfältiger (Soll)', value: '26,67x' },
                { label: 'Nettoanfangsrendite', value: '3,66 %' },
            ],
        },
    ],
};

const propertyManagement: SamplePerspectiveContent = {
    headline: 'NOI vor Capex 336.843 € über die Halteperiode 2018–2024',
    highlights: [
        { label: 'NOI-Marge Ø', value: '86 %' },
        { label: 'NOI-Rendite Ø', value: '4,20 %' },
        { label: 'Bewirtschaftung', value: '65.748 €' },
    ],
    sections: [
        {
            title: 'Kostenansätze',
            metrics: [
                { label: 'Verwaltung Alleineigentum', value: '250 €' },
                { label: 'Verwaltung Teileigentum', value: '500 €' },
                { label: 'Instandhaltung / -setzung Gebäude', value: '5,83 €/m²' },
                { label: 'Mietausfall', value: '2,00 %' },
                { label: 'Betriebskosten Leerstand', value: '1,48 €/m²' },
                { label: 'Mieterwechselkosten', value: '34 €/m²' },
                { label: 'Mieterwechselkosten WEG', value: '250 €/m²' },
            ],
        },
        {
            title: 'Ergebnis der Halteperiode',
            metrics: [
                { label: 'Bewirtschaftungskosten gesamt', value: '−65.748 €' },
                { label: 'Aktivierte Mieterwechselkosten', value: '11.414 €' },
                { label: 'NOI vor Capex', value: '336.843 €' },
                { label: 'NOI-Marge Ø', value: '86 %' },
                { label: 'NOI-Rendite Ø', value: '4,20 %' },
                { label: 'Capex', value: '−11.414 €' },
            ],
        },
    ],
    notes: [
        'Technische Due Diligence: Note 2,61; kein Instandhaltungsstau in den Gewerken ausgewiesen.',
        'An 6 Lüftungsgeräten fehlen gemäß Wartungsprotokoll die Abstandshalter — der Eigentümer übernimmt die Mängelbeseitigung in Eigenregie.',
        'Hausmeister- und Hausverwaltungsvertrag (Kündigungsfrist 3 Monate bzw. quartalsweise) sowie Heizungs- und Lüftungswartungsvertrag werden laut Kaufvertragsentwurf nicht übernommen.',
    ],
};

const financing: SamplePerspectiveContent = {
    headline: '40 % LTV, IRR levered 9,30 %',
    highlights: [
        { label: 'Darlehen', value: '400.000 €' },
        { label: 'Zinssatz', value: '1,40 %' },
        { label: 'IRR levered', value: '9,30 %' },
    ],
    sections: [
        {
            title: 'Darlehen',
            metrics: [
                { label: 'Darlehensbetrag', value: '400.000 €' },
                { label: 'LTV', value: '40,0 %' },
                { label: 'Zinsen', value: '1,40 %' },
                { label: 'Tilgung', value: '1,00 %' },
                { label: 'Kapitaldienst gesamt', value: '2,40 %' },
            ],
        },
        {
            title: 'Rendite',
            metrics: [
                { label: 'IRR unlevered', value: '6,81 %' },
                { label: 'NOI unlevered', value: '4,17 %' },
                { label: 'xEquity unlevered', value: '1,52x' },
                { label: 'IRR levered', value: '9,30 %' },
                { label: 'NOI levered', value: '5,16 %' },
                { label: 'xEquity levered', value: '1,74x' },
                { label: 'FFO I', value: '5,67 %' },
            ],
        },
        {
            title: 'Exit',
            metrics: [
                { label: 'Exitwert', value: '1.414.909 €' },
                { label: 'Vervielfältiger', value: '23,84x' },
                { label: 'Veräußerungskosten', value: '14.149 €' },
                { label: 'Veräußerungserlös', value: '1.400.760 €' },
            ],
        },
    ],
};

/** Sample content by process type → step → perspective. */
const sampleContent: Record<string, Record<string, Record<string, SamplePerspectiveContent>>> = {
    acquisition: {
        valuation: {
            microLocation,
            macroLocation,
            letting,
            acquisitionCosts,
            propertyManagement,
            financing,
        },
    },
};

export const sampleContentFor = (
    typeKey: string,
    stepKey: string,
    perspectiveKey: string,
): SamplePerspectiveContent | undefined => sampleContent[typeKey]?.[stepKey]?.[perspectiveKey];

export const hasSampleContent = (typeKey: string, stepKey: string): boolean =>
    !!sampleContent[typeKey]?.[stepKey];
